'use client';

import * as Sentry from '@sentry/nextjs';
import { useCallback, useEffect } from 'react';

import { useSwrMutation } from './swr';
import { AnalyticsEventName } from '@/api/analytics';
import {
  ConfirmLogin as ConfirmLoginInterface,
  login,
  LoginForm as LoginFormInterface,
  User,
} from '@/api/auth';
import {
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS,
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL,
} from '@/helpers/constants';
import { useAnalytics } from '@/hooks/analytics';

const ERRORS_COUNT_STORAGE_NAME = 'login';

const saveErrorsCountToStorage =
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS && AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL
    ? {
        storageName: ERRORS_COUNT_STORAGE_NAME,
        timeLeftToClear: AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL,
      }
    : undefined;

export const useLogin = () => {
  const { sendEventToAnalytics } = useAnalytics();

  const {
    trigger: requestLogin,
    requestCount,
    resetRequestCount,
    data,
    error,
    isMutating,
  } = useSwrMutation<User>({
    key: 'loginKey',
    fetcher: login,
    options: { populateCache: true, saveErrorsCountToStorage },
  });

  const handleLogin = useCallback(
    async (values: LoginFormInterface | ConfirmLoginInterface) => {
      try {
        await requestLogin(values);
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setTags({
            category: 'login',
            pathToFile: 'src/hooks/login.ts',
          });

          scope.setLevel('error');

          Sentry.captureMessage(
            `Error in login request - ${JSON.stringify(error)}`
          );
        });
      }
    },
    [requestLogin]
  );

  useEffect(() => {
    if (data) {
      resetRequestCount();
    }

    if (data?.id != null) {
      sendEventToAnalytics({ name: AnalyticsEventName.LOGIN_SUCCESS });
    }
  }, [data, resetRequestCount, sendEventToAnalytics]);

  useEffect(() => {
    if (error) {
      const errorJSON = JSON.stringify(error);
      const attributes = { additionalData: errorJSON };
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__LOGIN__FORM__ERROR,
        attributes,
      });
    }
  }, [error, sendEventToAnalytics]);

  return {
    handleLogin,
    userData: data,
    loginError: error,
    loginPending: isMutating,
    requestCount,
    resetRequestCount,
  };
};
